import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type { RotationData, PlayerRotation, SelectedPlayer } from './types';
import { TEAM_COLORS, ensureVisibleColor, ROTATION_COLORS } from './constants';
import { getTeamInfo } from '../stokasticProps/data/nbaTeams';
import ScoreRibbon from './ScoreRibbon';
import PeriodHeaders from './PeriodHeaders';
import TeamSection from './TeamSection';
import LineupAnalysisPanel from './LineupAnalysisPanel';

const teamLogoStyle = {
  width: 28,
  height: 28,
  objectFit: 'contain' as const,
};

interface RotationChartProps {
  data: RotationData;
}

const RotationChart: React.FC<RotationChartProps> = ({ data }) => {
  const { gameInfo, homeRotations, awayRotations, scoreTimeline, gameDuration } = data;

  const homeColor = ensureVisibleColor(TEAM_COLORS[gameInfo.home.abbrev] || '#10B981');
  const awayColor = ensureVisibleColor(TEAM_COLORS[gameInfo.away.abbrev] || '#EF4444');

  const formattedDate = useMemo(() => {
    const d = new Date(gameInfo.date + 'T12:00:00');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }, [gameInfo.date]);

  const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayer[]>([]);

  const selectedPlayerNames = useMemo(
    () => new Set(selectedPlayers.map((p) => p.name)),
    [selectedPlayers],
  );

  const panelOpen = selectedPlayers.length > 0;

  const handlePlayerSelect = useCallback(
    (player: PlayerRotation, teamAbbrev: string, _e: React.MouseEvent) => {
      setSelectedPlayers((prev) => {
        const existing = prev.find((p) => p.name === player.playerName);
        if (existing) {
          return prev.filter((p) => p.name !== player.playerName);
        }
        return [...prev, { name: player.playerName, teamAbbrev, stints: player.stints }];
      });
    },
    [],
  );

  const handleRemovePlayer = useCallback((name: string) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.name !== name));
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedPlayers([]);
  }, []);

  return (
    <>
      <Box
        sx={{
          flex: 1,
          backgroundColor: ROTATION_COLORS.bg.container,
          borderRadius: 1,
          p: 2,
          transition: 'margin-right 0.2s',
          ...(panelOpen ? { mr: '360px' } : {}),
        }}
      >
        {/* Game Header */}
        <Stack
          direction="row"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid rgba(58, 63, 75, 0.5)',
          }}
        >
          <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src={getTeamInfo(gameInfo.away.abbrev).logo}
              alt={gameInfo.away.abbrev}
              style={teamLogoStyle}
            />
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: ROTATION_COLORS.text.primary }}>
              {gameInfo.away.score}
            </Typography>
            <Typography sx={{ color: '#4B5563', mx: 1 }}>@</Typography>
            <img
              src={getTeamInfo(gameInfo.home.abbrev).logo}
              alt={gameInfo.home.abbrev}
              style={teamLogoStyle}
            />
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: ROTATION_COLORS.text.primary }}>
              {gameInfo.home.score}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.secondary }}>
              {formattedDate}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 600,
                color: '#F87171',
                backgroundColor: 'rgba(248, 113, 113, 0.12)',
                padding: '2px 8px',
                borderRadius: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {gameInfo.isFinal ? 'Final' : 'Live'}
            </Typography>
          </Stack>
        </Stack>

        <PeriodHeaders gameDuration={gameDuration} />

        <TeamSection
          players={awayRotations}
          opponentPlayers={homeRotations}
          teamAbbrev={gameInfo.away.abbrev}
          opponentAbbrev={gameInfo.home.abbrev}
          teamScore={gameInfo.away.score}
          gameDuration={gameDuration}
          isHome={false}
          selectedPlayerNames={selectedPlayerNames}
          onPlayerSelect={handlePlayerSelect}
        />

        {scoreTimeline && scoreTimeline.length > 0 && (
          <ScoreRibbon
            data={scoreTimeline}
            homeTeamColor={homeColor}
            awayTeamColor={awayColor}
            gameDuration={gameDuration}
            homeAbbrev={gameInfo.home.abbrev}
            awayAbbrev={gameInfo.away.abbrev}
          />
        )}

        <TeamSection
          players={homeRotations}
          opponentPlayers={awayRotations}
          teamAbbrev={gameInfo.home.abbrev}
          opponentAbbrev={gameInfo.away.abbrev}
          teamScore={gameInfo.home.score}
          gameDuration={gameDuration}
          isHome={true}
          selectedPlayerNames={selectedPlayerNames}
          onPlayerSelect={handlePlayerSelect}
        />
      </Box>

      {panelOpen && (
        <LineupAnalysisPanel
          selectedPlayers={selectedPlayers}
          homeRotations={homeRotations}
          awayRotations={awayRotations}
          homeAbbrev={gameInfo.home.abbrev}
          awayAbbrev={gameInfo.away.abbrev}
          onRemovePlayer={handleRemovePlayer}
          onClose={handleClosePanel}
        />
      )}
    </>
  );
};

export default RotationChart;
